/**
 * Devanagari → canonical gazetteer city names (policy-as-data).
 *
 * WHY: the birth-place gazetteer (the local 3,776-city index in
 * `public/data/place-cities.json` and the D1 `/_places` worker) is English-only,
 * so a seeker on /hi who types "मुंबई" never matched anything — the autocomplete
 * stayed empty and the submit-time resolver returned `empty`, surfacing the same
 * "pick your city" error as a misspelling. This table lets Devanagari input
 * resolve to the SAME canonical gazetteer row the English spelling resolves to.
 *
 * Discipline:
 *   - VALUES are canonical `place-cities.json` names — pinned by
 *     `place-devanagari.test.ts`, which fails if any value is not an exact
 *     gazetteer `name` (so a typo here cannot silently mis-route a chart).
 *   - KEYS are common Devanagari spellings (incl. anusvara/conjunct + nukta
 *     variants). Lookups run through `normalizeDevanagariKey`, which strips
 *     nukta + joiners, so "गाजियाबाद" and "गाज़ियाबाद" both hit one entry.
 *   - This is a closed exact-match table, NEVER fuzzy transliteration: an
 *     unknown Devanagari city stays unresolved and surfaces the conservative
 *     "confirm your city" path (chart accuracy > completion, rule 29). Where an
 *     Indian city name is genuinely shared across states (Aurangabad, …) the
 *     mapped English query flows through the SAME confidence rules as typed
 *     English — no new guessing is introduced.
 *
 * These are city-name transliterations (stable cultural facts), not computed
 * Vedic facts — rules 23/24 (engine-computed dates/positions) do not apply.
 * Coverage: the top Indian cities by population (every state capital + every
 * city ≥ ~500k) — the head of the /hi birth-place distribution.
 */

/** Raw authoring table: Devanagari spelling(s) → canonical gazetteer name. */
export const DEVANAGARI_CITY_ENTRIES: ReadonlyArray<readonly [string, string]> = [
  // Metros + million-plus cities
  ["मुंबई", "Mumbai"],
  ["मुम्बई", "Mumbai"],
  ["दिल्ली", "Delhi"],
  ["नई दिल्ली", "New Delhi"],
  ["बेंगलुरु", "Bengaluru"],
  ["बेंगलूरु", "Bengaluru"],
  ["बंगलौर", "Bengaluru"],
  ["बैंगलोर", "Bengaluru"],
  ["कोलकाता", "Kolkata"],
  ["कलकत्ता", "Kolkata"],
  ["चेन्नई", "Chennai"],
  ["चेन्नै", "Chennai"],
  ["हैदराबाद", "Hyderabad"],
  ["अहमदाबाद", "Ahmedabad"],
  ["पुणे", "Pune"],
  ["पूना", "Pune"],
  ["सूरत", "Surat"],
  ["जयपुर", "Jaipur"],
  ["लखनऊ", "Lucknow"],
  ["कानपुर", "Kanpur"],
  ["नागपुर", "Nagpur"],
  ["इंदौर", "Indore"],
  ["इन्दौर", "Indore"],
  ["ठाणे", "Thane"],
  ["थाने", "Thane"],
  ["भोपाल", "Bhopal"],
  ["विशाखापत्तनम", "Visakhapatnam"],
  ["विशाखापटनम", "Visakhapatnam"],
  ["पटना", "Patna"],
  ["वडोदरा", "Vadodara"],
  ["बड़ौदा", "Vadodara"],
  ["गाजियाबाद", "Ghaziabad"],
  ["लुधियाना", "Ludhiana"],
  ["आगरा", "Agra"],
  ["नासिक", "Nashik"],
  ["नाशिक", "Nashik"],
  ["फरीदाबाद", "Faridabad"],
  ["मेरठ", "Meerut"],
  ["राजकोट", "Rajkot"],
  ["वाराणसी", "Varanasi"],
  ["बनारस", "Varanasi"],
  ["श्रीनगर", "Srinagar"],
  ["औरंगाबाद", "Aurangabad"],
  ["धनबाद", "Dhanbad"],
  ["अमृतसर", "Amritsar"],
  ["प्रयागराज", "Prayagraj"],
  ["इलाहाबाद", "Prayagraj"],
  ["रांची", "Ranchi"],
  ["राँची", "Ranchi"],
  ["हावड़ा", "Howrah"],
  ["कोयंबटूर", "Coimbatore"],
  ["कोयम्बटूर", "Coimbatore"],
  ["जबलपुर", "Jabalpur"],
  ["ग्वालियर", "Gwalior"],
  ["विजयवाड़ा", "Vijayawada"],
  ["जोधपुर", "Jodhpur"],
  ["मदुरै", "Madurai"],
  ["मदुरई", "Madurai"],
  ["रायपुर", "Raipur"],
  ["कोटा", "Kota"],
  ["गुवाहाटी", "Guwahati"],
  ["चंडीगढ़", "Chandigarh"],
  ["चण्डीगढ़", "Chandigarh"],
  ["सोलापुर", "Sholapur"],
  ["शोलापुर", "Sholapur"],
  ["हुबली", "Hubballi"],
  ["हुब्बल्ली", "Hubballi"],
  ["तिरुचिरापल्ली", "Tiruchirappalli"],
  ["बरेली", "Bareilly"],
  ["मैसूर", "Mysuru"],
  ["मैसूरु", "Mysuru"],
  ["गुरुग्राम", "Gurugram"],
  ["गुड़गांव", "Gurugram"],
  ["गुड़गाँव", "Gurugram"],
  ["अलीगढ़", "Aligarh"],
  ["जालंधर", "Jalandhar"],
  ["जालन्धर", "Jalandhar"],
  ["भुवनेश्वर", "Bhubaneswar"],
  ["सलेम", "Salem"],
  ["वारंगल", "Warangal"],
  ["तिरुवनंतपुरम", "Thiruvananthapuram"],
  ["त्रिवेंद्रम", "Thiruvananthapuram"],
  ["गोरखपुर", "Gorakhpur"],
  ["गुंटूर", "Guntur"],
  ["बीकानेर", "Bikaner"],
  ["अमरावती", "Amravati"],
  ["नोएडा", "Noida"],
  ["जमशेदपुर", "Jamshedpur"],
  ["भिलाई", "Bhilai"],
  ["कटक", "Cuttack"],
  ["कोच्चि", "Kochi"],
  ["कोचीन", "Kochi"],
  ["भावनगर", "Bhavnagar"],
  ["देहरादून", "Dehradun"],
  ["दुर्गापुर", "Durgapur"],
  ["आसनसोल", "Asansol"],
  ["राउरकेला", "Rourkela"],
  ["नांदेड़", "Nanded"],
  ["कोल्हापुर", "Kolhapur"],
  ["अजमेर", "Ajmer"],
  ["अकोला", "Akola"],
  ["जामनगर", "Jamnagar"],
  ["उज्जैन", "Ujjain"],
  ["सिलीगुड़ी", "Siliguri"],
  ["झांसी", "Jhansi"],
  ["झाँसी", "Jhansi"],
  ["जम्मू", "Jammu"],
  ["सांगली", "Sangli"],
  ["मंगलुरु", "Mangaluru"],
  ["मंगलौर", "Mangaluru"],
  ["इरोड", "Erode"],
  ["बेलगावी", "Belagavi"],
  ["बेलगाम", "Belagavi"],
  ["तिरुनेलवेली", "Tirunelveli"],
  ["मालेगांव", "Malegaon"],
  ["मालेगाँव", "Malegaon"],
  ["गया", "Gaya"],
  ["उदयपुर", "Udaipur"],
  ["कुरनूल", "Kurnool"],
  // North-belt + state-capital coverage (heavy /hi traffic share)
  ["करनाल", "Karnal"],
  ["पटियाला", "Patiala"],
  ["मथुरा", "Mathura"],
  ["हरिद्वार", "Haridwar"],
  ["ऋषिकेश", "Rishikesh"],
  ["शिमला", "Shimla"],
  ["मुरादाबाद", "Moradabad"],
  ["सहारनपुर", "Saharanpur"],
  ["मुजफ्फरनगर", "Muzaffarnagar"],
  ["मुजफ्फरपुर", "Muzaffarpur"],
  ["भागलपुर", "Bhagalpur"],
  ["दरभंगा", "Darbhanga"],
  ["बोकारो", "Bokaro"],
  ["रोहतक", "Rohtak"],
  ["हिसार", "Hisar"],
  ["पानीपत", "Panipat"],
  ["सोनीपत", "Sonipat"],
  ["अंबाला", "Ambala"],
  ["अम्बाला", "Ambala"],
  ["वेल्लोर", "Vellore"],
  ["तिरुपति", "Tirupati"],
  ["नेल्लोर", "Nellore"],
  ["अनंतपुर", "Anantapur"],
  ["सतना", "Satna"],
  ["रीवा", "Rewa"],
  ["सागर", "Sagar"],
  ["रतलाम", "Ratlam"],
  ["देवास", "Dewas"],
  ["भीलवाड़ा", "Bhilwara"],
  ["अलवर", "Alwar"],
  ["सीकर", "Sikar"],
  ["पाली", "Pali"],
  ["बिलासपुर", "Bilaspur"],
  ["कोरबा", "Korba"],
  ["संबलपुर", "Sambalpur"],
  ["सम्बलपुर", "Sambalpur"],
  ["फिरोजाबाद", "Firozabad"],
  ["इंफाल", "Imphal"],
  ["इम्फाल", "Imphal"],
  ["शिलांग", "Shillong"],
  ["अगरतला", "Agartala"],
  ["गंगटोक", "Gangtok"],
  ["आइजोल", "Aizawl"],
  ["ईटानगर", "Itanagar"],
  ["कोहिमा", "Kohima"],
  ["पुडुचेरी", "Puducherry"],
  ["पांडिचेरी", "Puducherry"],
];
